import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Download, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { formatErr } from '../billing/shared';
import { ImportReportView } from './ImportReport';

export const SUMMARY = {
  customers: (r) => `${r.customers_created} new, ${r.customers_merged} merged, ${r.vehicles_added} vehicles`,
  balances: (r) => `₹ ${Number(r.total_due || 0).toLocaleString('en-IN')} due across ${r.customers_with_dues} customers`,
  stock: (r) => `${r.parts_created} new, ${r.parts_updated} updated parts`,
};

const download = async (kind, authHeader) => {
  try {
    const { data } = await axios.get(`${API}/self-import/template/${kind}`, { headers: authHeader(), responseType: 'blob' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(data); a.download = `${kind}-template.xlsx`; a.click();
  } catch (e) { toast.error(formatErr(e)); }
};

// Upload a sheet, match its columns, preview, import into this workshop.
export const SelfImportCard = ({ kind, title, hint, last, onDone }) => {
  const { authHeader } = useAuth();
  const [file, setFile] = useState(null);
  const [prev, setPrev] = useState(null);
  const [map, setMap] = useState({});
  const [cutoff, setCutoff] = useState('');
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);
  const body = (extra = {}) => { const b = new FormData(); b.append('kind', kind); b.append('file', file); Object.entries(extra).forEach(([k, v]) => b.append(k, v)); return b; };
  const preview = async (f) => {
    setFile(f); setResult(null); if (!f) return;
    setBusy('preview');
    try {
      const b = new FormData(); b.append('kind', kind); b.append('file', f);
      const { data } = await axios.post(`${API}/self-import/preview`, b, { headers: authHeader() });
      setPrev(data); setMap(data.mapping);
    } catch (e) { setPrev(null); toast.error(formatErr(e)); } finally { setBusy(''); }
  };
  const run = async () => {
    setBusy('run');
    try {
      const { data } = await axios.post(`${API}/self-import/run`, body({ mapping: JSON.stringify(map), cutoff }), { headers: authHeader() });
      setResult(data); setPrev(null); toast.success(`Imported: ${SUMMARY[kind](data)}`); onDone?.();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(''); }
  };
  const shown = prev ? prev.fields.filter((f) => map[f.key]) : [];
  return (
    <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-5" data-testid={`self-import-${kind}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="flex items-center gap-2 text-sm font-bold text-slate-900">{title}{last && <CheckCircle2 size={15} className="text-emerald-600" data-testid={`self-import-done-${kind}`} />}</h2>
          <p className="text-xs text-slate-500">{hint}</p>
          {last && <p className="mt-1 text-[11px] text-emerald-700">Last import {String(last.at).slice(0, 10)}: {SUMMARY[kind](last)}</p>}
        </div>
        <button type="button" onClick={() => download(kind, authHeader)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-400" data-testid={`self-import-template-${kind}`}><Download size={13} /> Template</button>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-700" data-testid={`self-import-pick-${kind}`}>
          {busy === 'preview' ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload Excel / CSV
          <input type="file" accept=".csv,.xlsx" hidden onChange={(e) => { preview(e.target.files?.[0]); e.target.value = ''; }} data-testid={`self-import-file-${kind}`} />
        </label>
      </div>
      {prev && <>
        <p className="text-xs text-slate-600">We matched your columns. Fix any that are wrong, check the preview, then import.</p>
        <div className="grid gap-2 sm:grid-cols-3" data-testid={`self-import-mapping-${kind}`}>
          {prev.fields.map((f) => (
            <label key={f.key} className="text-xs text-slate-600">{f.label}{f.required && ' *'}
              <select value={map[f.key] || ''} onChange={(e) => setMap((m) => ({ ...m, [f.key]: e.target.value }))} className="mt-0.5 block w-full rounded border border-slate-300 px-1.5 py-1 text-sm" data-testid={`self-map-${kind}-${f.key}`}>
                <option value="">Not in file</option>{prev.headers.map((h) => <option key={h} value={h}>{h}</option>)}
              </select></label>))}
        </div>
        <div className="max-h-56 overflow-auto rounded border border-slate-200">
          <table className="w-full text-xs"><thead className="sticky top-0 bg-slate-50"><tr>{shown.map((f) => <th key={f.key} className="px-2 py-1 text-left">{f.label}</th>)}</tr></thead>
            <tbody>{prev.rows.map((r, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={i} className="border-t border-slate-100">{shown.map((f) => <td key={f.key} className={`px-2 py-1 ${f.required && !r[map[f.key]] ? 'bg-amber-50' : ''}`}>{r[map[f.key]]}</td>)}</tr>))}</tbody></table>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {kind === 'balances' && <label className="text-xs text-slate-600">Balances as on <input type="date" value={cutoff} onChange={(e) => setCutoff(e.target.value)} className="ml-1 rounded border border-slate-300 px-1.5 py-1" data-testid="self-import-cutoff" /></label>}
          <span className="text-xs text-slate-500">First {prev.rows.length} of {prev.total} rows. {kind === 'stock' ? 'Same part number or name is updated, not doubled.' : 'Same phone or registration is merged, not doubled.'}</span>
          <button type="button" onClick={run} disabled={!!busy} className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid={`self-import-run-${kind}`}>
            {busy === 'run' ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Import {prev.total} rows</button>
        </div>
      </>}
      {result && (result.report ? <ImportReportView id={result.report.id} /> : (
        <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid={`self-import-result-${kind}`}>
          {SUMMARY[kind](result)}{result.issue_count ? ` · ${result.issue_count} row(s) flagged` : ''}
          {!!result.issues?.length && <ul className="mt-1 max-h-32 list-disc overflow-auto pl-5 text-xs text-amber-800">{result.issues.slice(0, 30).map((x, i) => <li key={i}>{x.row ? `Row ${x.row}: ` : ''}{x.issue}</li>)}</ul>}
        </div>))}
    </section>
  );
};
